import { BaseContextValue } from 'features/reporting/types'
import { useRolodexEntries } from 'features/rolodex'
import compact from 'lodash/compact'
import { useLockManagement } from 'pages/reporting/shared/hooks/useLockManagement'
import { useMetadataEntry } from 'pages/reporting/shared/hooks/useMetadataEntry'
import { usePdfManagement } from 'pages/reporting/shared/hooks/UsePDFManagement'
import { buildReportPdfFilename } from 'pages/reporting/shared/utils/utils'
import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import {
  GeckoSite,
  GeckoVisualInspection,
  GeckoVisualInspectionFieldResponse,
  GeckoVisualInspectionFormResponse,
  GeckoVisualInspectionSectionResponse,
  RolodexEntryType,
  SignatureData,
  SignoffState,
} from 'types'

// Import hooks
import { useFormData } from './hooks/useFormData'
import { useReportNavigation } from './hooks/useReportNavigation'
import {
  useReportRevisions,
  VisualSectionSelection,
} from './hooks/useReportRevisions'
import { useReportSubmission } from './hooks/useReportSubmission'
import {
  useVisualInspectionImages,
  VisualInspectionImage,
} from './hooks/useVisualInspectionImages'
// Import report structure
import { visualReportForSPA } from './visualReport'
// Import form data types
import {
  CoverSectionData,
  InspectionTeamMember,
  MainInfo,
  ReferenceDocument,
  VisualInspectionFormDataState,
  VisualInspectionRecommendation,
} from './VisualReportData'

export interface VisualReportContextValue extends BaseContextValue<VisualSectionSelection> {
  // Form Data
  formData: VisualInspectionFormDataState
  updateFormData: (newData: Partial<VisualInspectionFormDataState>) => void
  handleSignoffFinish: (values: SignatureData) => Promise<SignoffState>
  revertFormToCantilever: () => Promise<void>
  copyRevisionContent: (sections: VisualSectionSelection) => Promise<void>

  // Section handlers
  handleCoverSectionFinish: (values: CoverSectionData) => Promise<void>
  handleChecklistFindingsFinish: (scope: string) => Promise<void>
  handleAssetCorrosionAnalysisFinish: (analysis: string) => Promise<void>
  handleFindingsFinish: (findings: string) => Promise<void>
  handleRecommendationsFinish: (
    recommendations: VisualInspectionRecommendation[]
  ) => Promise<void>
  handlePressureTestSubInspectionsFinish: (
    pressureTestSubInspection: string
  ) => Promise<void>
  handleBundleSubInspectionsFinish: (
    bundleSubInspection: string
  ) => Promise<void>
  handleInspectionTeamFinish: (
    inspectionTeamMembers: InspectionTeamMember[]
  ) => Promise<void>
  handleReferenceDocumentsFinish: (
    referenceDocuments: ReferenceDocument[]
  ) => Promise<void>

  selectedRevisionData: VisualInspectionFormDataState | undefined

  // Visual Inspection Data
  selectedVisualInspection: GeckoVisualInspection | undefined
  isLoadingInspection: boolean
  formResponse?: GeckoVisualInspectionFormResponse
  sectionResponses: GeckoVisualInspectionSectionResponse[]
  fieldResponses: GeckoVisualInspectionFieldResponse[]
  visualInspectionImages: {
    images: VisualInspectionImage[]
    isLoading: boolean
  }

  // Cantilever Data
  hasCantileverData: boolean
  prePopulatedData: Partial<MainInfo & CoverSectionData>
  site: GeckoSite | undefined
}

interface VisualReportProviderProps {
  children: React.ReactNode
  reportId: string
  requestedMetadataEntryId: string | undefined
  siteId: string
}

export const VisualReportContext = createContext<
  VisualReportContextValue | undefined
>(undefined)

export const VisualReportProvider: FC<VisualReportProviderProps> = ({
  children,
  reportId,
  requestedMetadataEntryId,
  siteId,
}) => {
  const { getSite } = useRolodexEntries(
    siteId
      ? {
          type: RolodexEntryType.SITE,
          ids: [siteId],
        }
      : undefined
  )
  const site = useMemo(() => {
    return siteId ? getSite(siteId) : undefined
  }, [getSite, siteId])
  const { organizations } = useRolodexEntries(
    site?.tags.organization
      ? {
          type: RolodexEntryType.ORGANIZATION,
          ids: [site.tags.organization],
        }
      : undefined
  )
  const organization = organizations.at(0)
  const {
    metadataEntryId,
    metadataEntries,
    isLoading: isLoadingMetadata,
    publishRevision,
    revisions,
    currentRevisionNumber,
    createNewRevision,
    latestRevision,
    isCurrentRevisionPublished,
  } = useMetadataEntry(reportId, siteId, requestedMetadataEntryId)

  const currentMetadataRevision = useMemo(() => {
    return metadataEntries.find((entry) => entry.id === latestRevision?.id)
  }, [metadataEntries, latestRevision])

  // Get visual inspection IDs from metadata
  const visualInspectionIds = useMemo(() => {
    if (!currentMetadataRevision) return []

    return currentMetadataRevision.data.visualInspections
  }, [currentMetadataRevision])

  // Fetch visual inspection data
  const {
    getVisualInspection,
    visualInspectionFormResponses,
    visualInspectionSectionResponses,
    visualInspectionFieldResponses,
    isLoading: isLoadingInspections,
    units,
  } = useRolodexEntries(
    visualInspectionIds.length > 0
      ? [
          {
            type: RolodexEntryType.VISUAL_INSPECTION,
            ids: visualInspectionIds,
            links: [RolodexEntryType.UNIT],
          },
          {
            type: RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE,
            tags: {
              [RolodexEntryType.VISUAL_INSPECTION]: visualInspectionIds,
            },
          },
          {
            type: RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE,
            tags: {
              [RolodexEntryType.VISUAL_INSPECTION]: visualInspectionIds,
            },
          },
          {
            type: RolodexEntryType.VISUAL_INSPECTION_FIELD_RESPONSE,
            tags: {
              [RolodexEntryType.VISUAL_INSPECTION]: visualInspectionIds,
            },
          },
        ]
      : undefined
  )

  const unit = useMemo(() => units.at(0), [units])

  // Get the primary visual inspection (assume first one)
  const selectedVisualInspection = useMemo(() => {
    const latestEntry = metadataEntries.find(
      (entry) => entry.id === latestRevision?.id
    )
    if (!latestEntry) return undefined

    const firstVisualId = latestEntry.data.visualInspections.at(0)
    if (firstVisualId) {
      return getVisualInspection(firstVisualId)
    }

    return undefined
  }, [metadataEntries, latestRevision, getVisualInspection])

  const { formResponse, sectionResponses, fieldResponses } = useMemo(() => {
    if (!selectedVisualInspection)
      return {
        formResponse: undefined,
        sectionResponses: [],
        fieldResponses: [],
      }

    const formResponse = visualInspectionFormResponses.at(0)
    const sectionResponses = visualInspectionSectionResponses.filter(
      (response) =>
        response.tags['visual-inspection-form-response'] === formResponse?.id
    )
    const fieldResponses = visualInspectionFieldResponses.filter(
      (response) =>
        response.tags['visual-inspection-form-response'] === formResponse?.id
    )

    return { formResponse, sectionResponses, fieldResponses }
  }, [
    selectedVisualInspection,
    visualInspectionFormResponses,
    visualInspectionSectionResponses,
    visualInspectionFieldResponses,
  ])

  // Form data management
  const {
    formData,
    updateFormData,
    handleCoverSectionFinish,
    handleChecklistFindingsFinish,
    handleAssetCorrosionAnalysisFinish,
    handleFindingsFinish,
    handleRecommendationsFinish,
    handlePressureTestSubInspectionsFinish,
    handleBundleSubInspectionsFinish,
    handleInspectionTeamFinish,
    handleReferenceDocumentsFinish,
    handleSignoffFinish,
  } = useFormData(metadataEntryId, metadataEntries, isLoadingMetadata)

  // Pre-populate form data from visual inspection if empty
  useEffect(() => {
    if (
      selectedVisualInspection &&
      !formData.mainInfo?.asset &&
      !isLoadingMetadata
    ) {
      updateFormData({
        mainInfo: {
          asset: selectedVisualInspection.tags.unit,
          assetId: selectedVisualInspection.tags.unit,
        },
        coverSectionData: {
          asset: selectedVisualInspection.tags.unit,
          assetId: selectedVisualInspection.tags.unit,
          inspectionType: selectedVisualInspection.data.inspection_type,
        },
      })
    }
  }, [
    selectedVisualInspection,
    formData.mainInfo,
    updateFormData,
    isLoadingMetadata,
  ])

  const revertFormToCantilever = useCallback(async () => {
    // TODO: Implement revert logic
  }, [])

  // Use the visual report structure
  const report = visualReportForSPA
  const downloadFilename = buildReportPdfFilename({
    organizationName: organization?.data.name,
    siteName: site?.data.name,
    assetName: unit?.data.name || formData.mainInfo.asset,
    revisionNumber: currentRevisionNumber,
  })

  // Navigation
  const { activePage, navigateToPage, returnToMenu, completionStatus } =
    useReportNavigation({
      formData,
      report,
    })

  const {
    latestDraftTimestamp,
    latestDraftPath,
    isPublishing,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    handlePreviewLatestDraft,
    handlePreviewPublishedPdf,
  } = usePdfManagement({
    metadataEntryId,
    formData,
    report: visualReportForSPA,
    downloadFilename,
    onPublishedPathChange: (publishedPdfPath) =>
      updateFormData({ publishedPdfPath }),
  })

  // Submission hooks
  const {
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
  } = useReportSubmission({
    metadataEntryId,
    formData,
    updateFormData,
    currentRevisionNumber,
    pdfFilename: downloadFilename,
    publishLatestDraftPDF,
    publishRevision,
  })

  // Revision management
  const {
    selectedRevisionData,
    loadRevisionData,
    copyRevisionContent,
    hasPublishedRevisions,
    canRevertToCantilever,
  } = useReportRevisions({
    metadataEntries,
    currentRevisionNumber,
    revisions,
    onUpdateFormData: updateFormData,
  })

  // Lock management
  const {
    isLocked,
    startedSignoff,
    lastLockTimestamp,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,
  } = useLockManagement({
    metadataEntryId,
    metadataWithSignoff: {
      lockState: formData.lockState,
      signoff: formData.signoff,
    },
    updateFormData,
  })

  const lockState = { isLocked, startedSignoff, lastLockTimestamp }

  const pdfState = {
    hasPublishedPdf: !!formData.publishedPdfPath,
    isPublishing,
    latestDraftTimestamp,
    latestDraftPath,
    publishedPdfPath: formData.publishedPdfPath,
  }

  // Extract Cantilever data
  const hasCantileverData = !!selectedVisualInspection
  const prePopulatedData: Partial<MainInfo & CoverSectionData> = useMemo(() => {
    if (!selectedVisualInspection) return {}

    return {
      asset: selectedVisualInspection.tags.unit,
      assetId: selectedVisualInspection.tags.unit,
      inspectionType: selectedVisualInspection.data.inspection_type,
      extent: selectedVisualInspection.data.findings_summary,
    }
  }, [selectedVisualInspection])

  const visualInspectionImages = useVisualInspectionImages(
    selectedVisualInspection,
    site
  )

  const contextValue: VisualReportContextValue = {
    // Base context values
    activePage,
    report,
    navigateToPage,
    returnToMenu,
    completionStatus,

    // Form Data
    formData,
    updateFormData,
    handleSignoffFinish,
    revertFormToCantilever,

    // Section handlers
    handleCoverSectionFinish,
    handleChecklistFindingsFinish,
    handleAssetCorrosionAnalysisFinish,
    handleFindingsFinish,
    handleRecommendationsFinish,
    handlePressureTestSubInspectionsFinish,
    handleBundleSubInspectionsFinish,
    handleInspectionTeamFinish,
    handleReferenceDocumentsFinish,

    // Metadata
    metadataEntryId,
    isLoading: isLoadingMetadata || isLoadingInspections,
    currentRevisionNumber,
    inspections: compact([selectedVisualInspection]),

    // Submissions
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,

    // Revisions
    revisions,
    selectedRevisionData,
    loadRevisionData,
    copyRevisionContent,
    hasPublishedRevisions,
    canRevertToCantilever,
    createNewRevision,
    isCurrentRevisionPublished,

    // Visual inspection specific
    selectedVisualInspection,
    visualInspectionImages,
    formResponse,
    sectionResponses,
    fieldResponses,
    isLoadingInspection: isLoadingInspections,
    hasCantileverData,
    prePopulatedData,
    site,
    unit,
    // Lock Management
    lockState,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,

    // PDF Management
    pdfState,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    handlePreviewLatestDraft,
    handlePreviewPublishedPdf,
  }

  return (
    <VisualReportContext.Provider value={contextValue}>
      {children}
    </VisualReportContext.Provider>
  )
}

export const useVisualReportOps = (): VisualReportContextValue => {
  const context = React.useContext(VisualReportContext)
  if (!context) {
    throw new Error(
      'useVisualReportOps must be used within VisualReportProvider'
    )
  }
  return context
}
